export type ManualSelectionMorphologyOperation = 'dilate' | 'erode';

export type MorphSelectionEdit = {
  linearIndices: Uint32Array;
  segmentIndex: 0 | 1;
};

type Dimensions3D = readonly [number, number, number];

function getValidDimensions(
  dimensions: readonly number[],
): Dimensions3D | null {
  if (
    dimensions.length < 3 ||
    !dimensions.every(
      (dimension) => Number.isInteger(dimension) && dimension > 0,
    )
  ) {
    return null;
  }

  return [dimensions[0], dimensions[1], dimensions[2]];
}

function getVoxelCount([width, height, depth]: Dimensions3D) {
  return width * height * depth;
}

function linearIndexToIJK(
  index: number,
  [width, height]: Dimensions3D,
): [number, number, number] {
  const sliceSize = width * height;
  const k = Math.floor(index / sliceSize);
  const remainder = index - k * sliceSize;
  const j = Math.floor(remainder / width);
  const i = remainder - j * width;

  return [i, j, k];
}

function ijkToLinearIndex(
  i: number,
  j: number,
  k: number,
  [width, height]: Dimensions3D,
) {
  return (k * height + j) * width + i;
}

function collectValidSourceIndices(
  indices: ArrayLike<number>,
  voxelCount: number,
) {
  const source = new Set<number>();

  for (let idx = 0; idx < indices.length; idx += 1) {
    const index = indices[idx];
    if (!Number.isInteger(index) || index < 0 || index >= voxelCount) continue;
    source.add(index);
  }

  return source;
}

function sortedUint32Array(values: Iterable<number>) {
  return Uint32Array.from(Array.from(values).sort((a, b) => a - b));
}

function subtractLinearIndices(source: Set<number>, remove: Set<number>) {
  const result = new Set<number>();

  source.forEach((index) => {
    if (!remove.has(index)) {
      result.add(index);
    }
  });

  return result;
}

function dilateSelection(
  source: Set<number>,
  dimensions: Dimensions3D,
) {
  const [width, height, depth] = dimensions;
  const result = new Set<number>();

  source.forEach((index) => {
    const [i, j, k] = linearIndexToIJK(index, dimensions);

    for (let dz = -1; dz <= 1; dz += 1) {
      const nextK = k + dz;
      if (nextK < 0 || nextK >= depth) continue;

      for (let dy = -1; dy <= 1; dy += 1) {
        const nextJ = j + dy;
        if (nextJ < 0 || nextJ >= height) continue;

        for (let dx = -1; dx <= 1; dx += 1) {
          const nextI = i + dx;
          if (nextI < 0 || nextI >= width) continue;

          result.add(ijkToLinearIndex(nextI, nextJ, nextK, dimensions));
        }
      }
    }
  });

  return result;
}

function erodeSelection(
  source: Set<number>,
  dimensions: Dimensions3D,
) {
  const [width, height, depth] = dimensions;
  const result = new Set<number>();

  source.forEach((index) => {
    const [i, j, k] = linearIndexToIJK(index, dimensions);

    for (let dz = -1; dz <= 1; dz += 1) {
      const nextK = k + dz;
      if (nextK < 0 || nextK >= depth) return;

      for (let dy = -1; dy <= 1; dy += 1) {
        const nextJ = j + dy;
        if (nextJ < 0 || nextJ >= height) return;

        for (let dx = -1; dx <= 1; dx += 1) {
          const nextI = i + dx;
          if (nextI < 0 || nextI >= width) return;
          if (!source.has(ijkToLinearIndex(nextI, nextJ, nextK, dimensions))) {
            return;
          }
        }
      }
    }

    result.add(index);
  });

  return result;
}

export function morphSelectionLinearIndices3D(
  indices: ArrayLike<number>,
  dimensions: readonly number[],
  operation: ManualSelectionMorphologyOperation,
): Uint32Array {
  const validDimensions = getValidDimensions(dimensions);
  if (!validDimensions || !indices.length) return new Uint32Array();

  const source = collectValidSourceIndices(
    indices,
    getVoxelCount(validDimensions),
  );
  if (!source.size) return new Uint32Array();

  const result =
    operation === 'dilate'
      ? dilateSelection(source, validDimensions)
      : erodeSelection(source, validDimensions);

  return sortedUint32Array(result);
}

export function createMorphSelectionEdit(
  indices: ArrayLike<number>,
  dimensions: readonly number[],
  operation: ManualSelectionMorphologyOperation,
): MorphSelectionEdit {
  const validDimensions = getValidDimensions(dimensions);
  if (!validDimensions || !indices.length) {
    return {
      linearIndices: new Uint32Array(),
      segmentIndex: operation === 'dilate' ? 1 : 0,
    };
  }

  const source = collectValidSourceIndices(
    indices,
    getVoxelCount(validDimensions),
  );
  if (!source.size) {
    return {
      linearIndices: new Uint32Array(),
      segmentIndex: operation === 'dilate' ? 1 : 0,
    };
  }

  if (operation === 'erode') {
    const eroded = erodeSelection(source, validDimensions);
    return {
      linearIndices: sortedUint32Array(
        subtractLinearIndices(source, eroded),
      ),
      segmentIndex: 0,
    };
  }

  return {
    linearIndices: sortedUint32Array(dilateSelection(source, validDimensions)),
    segmentIndex: 1,
  };
}
